import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  acknowledgeAnnouncement,
  dismissAnnouncement,
  getActivePopupAnnouncements,
} from "../services/announcementService";

const activePopupCache = new Map();

const useAnnouncementPopup = (pagePath) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const lastFetchedPathRef = useRef("");

  const token = localStorage.getItem("token");
  const currentAnnouncement = useMemo(
    () => announcements[0] || null,
    [announcements]
  );

  const advanceQueue = useCallback(() => {
    setAnnouncements((prev) => prev.slice(1));
  }, []);

  const getCacheKey = useCallback(() => {
    if (!token || !pagePath) return "";
    return `${token}:${pagePath}`;
  }, [pagePath, token]);

  const fetchAnnouncements = useCallback(async () => {
    if (!token || !pagePath) {
      setAnnouncements([]);
      return;
    }

    const cacheKey = getCacheKey();
    const cachedValue = activePopupCache.get(cacheKey);

    if (cachedValue?.status === "resolved") {
      setAnnouncements(Array.isArray(cachedValue.data) ? cachedValue.data : []);
      return;
    }

    if (cachedValue?.status === "pending") {
      setLoading(true);

      try {
        const data = await cachedValue.promise;
        setAnnouncements(Array.isArray(data) ? data : []);
      } finally {
        setLoading(false);
      }

      return;
    }

    if (lastFetchedPathRef.current === pagePath) {
      return;
    }

    lastFetchedPathRef.current = pagePath;
    setLoading(true);

    try {
      const requestPromise = getActivePopupAnnouncements(pagePath).then(
        (response) => (Array.isArray(response?.data) ? response.data : [])
      );

      activePopupCache.set(cacheKey, {
        status: "pending",
        promise: requestPromise,
      });

      const data = await requestPromise;
      activePopupCache.set(cacheKey, {
        status: "resolved",
        data,
      });
      setAnnouncements(data);
    } catch (error) {
      console.error("Fetch active announcements error:", error);
      activePopupCache.delete(cacheKey);
      setAnnouncements([]);
      toast.error(
        error?.response?.data?.message || "โหลดประกาศระบบไม่สำเร็จ"
      );
    } finally {
      setLoading(false);
    }
  }, [getCacheKey, pagePath, token]);

  useEffect(() => {
    lastFetchedPathRef.current = "";
  }, [pagePath, token]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const clearCurrentRouteCache = useCallback(() => {
    const cacheKey = getCacheKey();
    if (cacheKey) {
      activePopupCache.delete(cacheKey);
    }
  }, [getCacheKey]);

  const handleDismiss = useCallback(async () => {
    if (!currentAnnouncement?._id) return;

    try {
      setActionLoading(true);
      await dismissAnnouncement(currentAnnouncement._id, pagePath);
      clearCurrentRouteCache();
      advanceQueue();
    } catch (error) {
      console.error("Dismiss announcement error:", error);
      toast.error(
        error?.response?.data?.message || "ไม่สามารถปิดประกาศได้"
      );
    } finally {
      setActionLoading(false);
    }
  }, [
    advanceQueue,
    clearCurrentRouteCache,
    currentAnnouncement?._id,
    pagePath,
  ]);

  const handleAcknowledge = useCallback(async () => {
    if (!currentAnnouncement?._id) return;

    try {
      setActionLoading(true);
      await acknowledgeAnnouncement(currentAnnouncement._id, pagePath);
      clearCurrentRouteCache();
      advanceQueue();
    } catch (error) {
      console.error("Acknowledge announcement error:", error);
      toast.error(
        error?.response?.data?.message || "ไม่สามารถรับทราบประกาศได้"
      );
    } finally {
      setActionLoading(false);
    }
  }, [
    advanceQueue,
    clearCurrentRouteCache,
    currentAnnouncement?._id,
    pagePath,
  ]);

  return {
    currentAnnouncement,
    hasAnnouncements: announcements.length > 0,
    loading,
    actionLoading,
    handleDismiss,
    handleAcknowledge,
    clearCurrentAnnouncement: advanceQueue,
  };
};

export default useAnnouncementPopup;
